import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  useEmbeddedEffect,
  MMEmbeddableZone,
} from '@machinemetrics/mm-react-embeddable';
import { throwSuccessToast } from 'lib/utils/toast';
import { useDispatch, useSelector } from 'react-redux';
import { actionSetPopupStack } from 'lib/actions';
import { getLatestJobId } from 'lib/selectors/getLatestJobId';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeNetPartsMade } from 'lib/selectors/getScopeNetPartsMade';
import i18n from 'i18next';

const CustomTabZone = ({ id, src, base }) => {
  const jobId = useSelector(getLatestJobId);
  const machine = useSelector(getMachine);
  const scopeNetPartsMade = useSelector(getScopeNetPartsMade);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [path, setPath] = useState();

  const isActive =
    location.pathname === base || location.pathname.startsWith(`${base}/`);

  const context = useMemo(() => {
    return {
      operationId: jobId,
      machineId: machine?.id,
      machineRef: machine?.machineRef,
      partCount: scopeNetPartsMade,
    };
  }, [jobId, machine, scopeNetPartsMade]);

  useEmbeddedEffect(id, 'set-path', (data) => {
    if (data.path.indexOf('/') === 0) {
      // The app reports its full path, src path included; the tab URL holds
      // only what follows it, or the src path doubles on every navigation.
      const srcRoot = new URL(src).pathname.replace(/\/$/, '');
      const rest = data.path.slice(srcRoot.length);
      const isUnderSrc =
        srcRoot && data.path.startsWith(srcRoot) && /^([/?#]|$)/.test(rest);
      const newPath = `${base}${isUnderSrc ? rest : data.path}`.replace(
        /\/$/,
        ''
      );
      history.push(newPath);
    } else {
      const u = new URL(data.path);
      const p = u.pathname.replace(/^\/app/, '');
      history.push(`${p}${u.search}`);
    }
  });

  useEmbeddedEffect(id, 'toast', ({ message }) => {
    throwSuccessToast(message);
  });

  useEmbeddedEffect(id, 'popup', ({ message }) => {
    dispatch(
      actionSetPopupStack({
        params: {
          message,
          popupType: 'message',
          order: 3,
        },
      })
    );
  });

  useEffect(() => {
    if (!location || !src) {
      return;
    }

    const u = new URL(src);
    const embeddedRootPath = u.pathname.replace(/\/$/, '');

    // Calculate the sub-route relative to the embedded app's root
    const subPath = isActive ? location.pathname.substring(base.length) : '';
    const query = isActive ? location.search : '';

    setPath(embeddedRootPath + subPath + query || '/');
  }, [src, location, base, isActive]);

  return (
    <MMEmbeddableZone
      id={id}
      src={src}
      path={path}
      context={context}
      params={{
        language: i18n.language,
        isVisible: isActive,
      }}
    />
  );
};

export { CustomTabZone };
